import { LogoMark } from '../components/nav/nav-parts.tsx';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { ThemeToggle } from '../components/app-shell.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { LOGIN_TEXTS, LoginError, stripCodeSpaces, type AuthClient } from '../lib/auth.ts';
import { OneTimeCodeInput } from './login.tsx';

export const SETUP_PATH = '/einrichten';

/** `token_hash` aus dem Fragment (`#token_hash=…&type=recovery`); das Fragment geht nie an einen Server. */
function tokenFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('token_hash');
  const type = params.get('type');
  return token && (type === null || type === 'recovery') ? token : null;
}

/** `code`: Konto hat schon eine Authenticator-App – Supabase ändert das Passwort erst mit Code (aal2). */
type Step = { kind: 'start' } | { kind: 'code'; factorId: string } | { kind: 'password' };

/**
 * Seite `/einrichten` (Story 1.7, D1): Einrichtungs-Link vom Admin oder aus „Passwort vergessen“.
 * Erst „Weiter“ löst den Link ein (Link-Vorschauen in Chat-Programmen verbrauchen ihn nicht), dann
 * setzt die Person ihr Passwort. Ist der zweite Faktor eingeschaltet, folgt danach die bestehende
 * Einrichtung der Authenticator-App (D5).
 */
export function SetupPage({ auth, configError, onDone }: { auth: AuthClient | null; configError: string | null; onDone: () => void }) {
  const [token] = useState(() => tokenFromHash(window.location.hash));
  const [step, setStep] = useState<Step>({ kind: 'start' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);

  async function redeem() {
    if (!auth || !token) return;
    setBusy(true);
    setError(null);
    try {
      await auth.redeemSetupLink(token);
      // Eingelöst: Token nicht in der Adresszeile stehen lassen.
      window.history.replaceState(null, '', SETUP_PATH);
      const factorId = await auth.verifiedFactorId();
      setStep(factorId ? { kind: 'code', factorId } : { kind: 'password' });
    } catch (err) {
      setError(err instanceof LoginError ? err : new LoginError(LOGIN_TEXTS.general));
    } finally {
      setBusy(false);
    }
  }

  async function confirmCode(factorId: string, code: string) {
    if (!auth) return;
    setBusy(true);
    setError(null);
    try {
      await auth.verifyCode(factorId, code);
      setStep({ kind: 'password' });
    } catch (err) {
      setError(err instanceof LoginError ? err : new LoginError(LOGIN_TEXTS.general));
    } finally {
      setBusy(false);
    }
  }

  async function save(password: string) {
    if (!auth) return;
    setBusy(true);
    setError(null);
    try {
      await auth.setPassword(password);
      onDone();
    } catch (err) {
      setError(err instanceof LoginError ? err : new LoginError(LOGIN_TEXTS.general));
      setBusy(false);
    }
  }

  let content: ReactNode;
  if (configError) {
    content = (
      <>
        <h1 className="mt-space-6 text-page-title leading-tight font-semibold tracking-[-0.025em]">Zugang einrichten</h1>
        <Alert text={configError} />
      </>
    );
  } else if (!token) {
    content = (
      <>
        <h1 className="mt-space-6 text-page-title leading-tight font-semibold tracking-[-0.025em]">Zugang einrichten</h1>
        <Alert text={LOGIN_TEXTS.linkBroken} />
      </>
    );
  } else if (step.kind === 'start') {
    const used = error?.message === LOGIN_TEXTS.linkUsed;
    content = (
      <>
        <h1 className="mt-space-6 text-page-title leading-tight font-semibold tracking-[-0.025em]">Zugang einrichten</h1>
        <p className="mt-space-3 text-row text-ink-2">
          Willkommen im ERP von TILL TOOLS. Mit diesem Link legst du dein Passwort fest. Der Link funktioniert nur einmal.
        </p>
        <Alert text={error?.message ?? null} />
        {used ? (
          <a href="/" className="mt-space-5 inline-block min-h-6 self-center text-body font-medium text-ink-2 hover:text-ink touch-hit">
            Zur Anmeldung
          </a>
        ) : (
          <Button className="mt-6 w-full" disabled={busy || !auth} onClick={() => void redeem()}>
            Weiter
          </Button>
        )}
      </>
    );
  } else if (step.kind === 'code') {
    const factorId = step.factorId;
    content = <CodeStep key={factorId} busy={busy} error={error} onSubmit={(code) => void confirmCode(factorId, code)} />;
  } else {
    content = <PasswordStep busy={busy} error={error} onSubmit={(p) => void save(p)} />;
  }

  return (
    // Redesign OpenAI-Stil (N40): wie die Anmeldung – ohne Karte, Zeichen mittig, große ruhige Überschrift.
    <main className="relative flex min-h-screen flex-col items-center px-space-6 pt-24 pb-space-8 tablet:justify-center tablet:pt-space-8">
      <div className="absolute top-space-6 right-space-6">
        <ThemeToggle />
      </div>
      <div className="flex animate-fade-in flex-col items-center gap-space-3">
        <span aria-hidden>
          <LogoMark />
        </span>
        <span className="type-wordmark whitespace-nowrap text-ink-2">TILL TOOLS ERP</span>
      </div>
      <section className="mt-space-4 flex w-full max-w-sm animate-fade-in flex-col [&_h1]:text-center [&>p]:text-center">{content}</section>
    </main>
  );
}

function CodeStep({ busy, error, onSubmit }: { busy: boolean; error: LoginError | null; onSubmit: (code: string) => void }) {
  const id = useId();
  const errorId = useId();
  const [code, setCode] = useState('');
  const codeError = error?.field === 'code' ? error.message : null;
  return (
    <>
      <h1 className="mt-space-6 text-page-title leading-tight font-semibold tracking-[-0.025em]">Code eingeben</h1>
      <p className="mt-space-3 text-row text-ink-2">
        Dein Zugang hat schon eine Authenticator-App. Gib den 6-stelligen Code für „TILL ERP“ ein, dann legst du dein neues Passwort fest.
      </p>
      <Alert text={error && error.field !== 'code' ? error.message : null} />
      <form
        className="mt-space-8 flex flex-col gap-space-5"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          onSubmit(stripCodeSpaces(code));
        }}
      >
        <div>
          <Label htmlFor={id}>Code aus der App</Label>
          <OneTimeCodeInput id={id} value={code} onChange={setCode} invalid={!!codeError} describedBy={codeError ? errorId : undefined} />
          {codeError ? (
            <p id={errorId} role="alert" className="type-meta mt-2 text-clarify">
              {codeError}
            </p>
          ) : null}
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          Bestätigen
        </Button>
      </form>
    </>
  );
}

function PasswordStep({ busy, error, onSubmit }: { busy: boolean; error: LoginError | null; onSubmit: (password: string) => void }) {
  const passwordId = useId();
  const repeatId = useId();
  const passwordErrorId = useId();
  const repeatErrorId = useId();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [local, setLocal] = useState<{ password?: string; repeat?: string }>({});

  const passwordError = local.password ?? (error?.field === 'password' ? error.message : null);
  const formError = error && error.field === null ? error.message : null;

  return (
    <>
      <h1 className="mt-space-6 text-page-title leading-tight font-semibold tracking-[-0.025em]">Passwort festlegen</h1>
      <p className="mt-space-3 text-row text-ink-2">
        Wähle ein langes Passwort, am besten aus einem Passwort-Manager. Damit meldest du dich künftig mit deiner E-Mail-Adresse an.
      </p>
      <Alert text={formError} />
      <form
        className="mt-space-8 flex flex-col gap-space-5"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!password) return setLocal({ password: LOGIN_TEXTS.passwordMissing });
          if (password !== repeat) return setLocal({ repeat: LOGIN_TEXTS.passwordMismatch });
          setLocal({});
          onSubmit(password);
        }}
      >
        <div>
          <Label htmlFor={passwordId}>Neues Passwort</Label>
          <Input
            id={passwordId}
            type="password"
            name="new-password"
            autoComplete="new-password"
            autoFocus
            invalid={!!passwordError}
            aria-describedby={passwordError ? passwordErrorId : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {passwordError ? (
            <p id={passwordErrorId} role="alert" className="type-meta mt-2 text-clarify">
              {passwordError}
            </p>
          ) : null}
        </div>
        <div>
          <Label htmlFor={repeatId}>Passwort wiederholen</Label>
          <Input
            id={repeatId}
            type="password"
            name="repeat-password"
            autoComplete="new-password"
            invalid={!!local.repeat}
            aria-describedby={local.repeat ? repeatErrorId : undefined}
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
          {local.repeat ? (
            <p id={repeatErrorId} role="alert" className="type-meta mt-2 text-clarify">
              {local.repeat}
            </p>
          ) : null}
        </div>
        <Button type="submit" className="w-full" disabled={busy}>
          Passwort speichern
        </Button>
      </form>
    </>
  );
}

function Alert({ text }: { text: string | null }): ReactNode {
  if (!text) return null;
  return (
    <p role="alert" className="type-meta mt-5 rounded-tile bg-clarify-bg px-4 py-3 text-clarify">
      {text}
    </p>
  );
}
